import { useMemo, type CSSProperties } from "react";

import { NO_LAYOUT, useGrid, useView } from "./virtual-view";
import { Slot, type CardType } from "./VirtualMasonry";
import masonry from "./VirtualMasonry.module.css";
import classes from "./VirtualRows.module.css";

/** Live's narrowest card in a row (`--rows-min-item-width`). */
export const MIN_CARD = 45;
/** Live's grow of the last line's phantom: it takes nearly all of that line's free space. */
const PHANTOM_GROW = 9999;

/** A card's place in its row: its item's index, its left edge and its width. */
export interface RowCell {
  index: number;
  left: number;
  width: number;
}

/** One row: its top, and its cards left to right. */
export interface PlacedRow {
  top: number;
  cells: RowCell[];
}

/**
 * Live's rows layout (`rows-layout`, spec library-rows), computed so only the rows near the view
 * are mounted. Each card is `height` tall, its basis `height` times its shape (1 when unknown), at
 * least `MIN_CARD` and at most half the width. The cards wrap into lines as flex-wrap breaks them,
 * `gap` apart, and each line's free space is shared by their shapes, a card held at half the
 * width. The last line's phantom (live's: grow 9999, basis 0, a gap after the last card) takes
 * nearly all of its free space, so that line keeps its cards near their basis; where the gap
 * leaves it no room, the phantom wraps alone and the last line fills as any other.
 */
export function placeRows(ratios: readonly number[], width: number, height: number, gap: number): PlacedRow[] {
  if (ratios.length === 0 || width <= 0) return [];
  const most = Math.max(MIN_CARD, width / 2);
  const shapes = ratios.map((ratio) => (ratio > 0 && Number.isFinite(ratio) ? ratio : 1));
  const basis = shapes.map((shape) => Math.min(most, Math.max(MIN_CARD, height * shape)));
  const lines: number[][] = [];
  let line: number[] = [];
  let used = 0;
  basis.forEach((size, index) => {
    if (line.length > 0 && used + gap + size > width) {
      lines.push(line);
      line = [];
      used = 0;
    }
    used += (line.length > 0 ? gap : 0) + size;
    line.push(index);
  });
  lines.push(line);
  const phantomJoins = used + gap <= width;
  return lines.map((indices, row) => {
    const phantom = phantomJoins && row === lines.length - 1;
    const gaps = gap * (indices.length - 1 + (phantom ? 1 : 0));
    const widths = grown(
      indices.map((at) => basis[at]!),
      indices.map((at) => shapes[at]!),
      width - gaps,
      most,
      phantom,
    );
    let left = 0;
    const cells = indices.map((index, at) => {
      const cell = { index, left, width: widths[at]! };
      left += widths[at]! + gap;
      return cell;
    });
    return { top: row * (height + gap), cells };
  });
}

/**
 * A line's widths once its free space is shared out, as flexbox resolves flexible lengths: by
 * grow (each card's its shape, the phantom's 9999), a card past `most` held there and the rest
 * shared again.
 */
function grown(sizes: readonly number[], grows: readonly number[], room: number, most: number, phantom: boolean): number[] {
  const widths = [...sizes];
  const open = new Set(sizes.map((_, at) => at));
  for (;;) {
    const held = widths.reduce((sum, each, at) => (open.has(at) ? sum : sum + each), 0);
    const free = room - held - [...open].reduce((sum, at) => sum + sizes[at]!, 0);
    if (free <= 0) return widths;
    const share = [...open].reduce((sum, at) => sum + grows[at]!, 0) + (phantom ? PHANTOM_GROW : 0);
    const over = [...open].filter((at) => sizes[at]! + (free * grows[at]!) / share > most);
    if (over.length === 0) {
      for (const at of open) widths[at] = sizes[at]! + (free * grows[at]!) / share;
      return widths;
    }
    for (const at of over) {
      widths[at] = most;
      open.delete(at);
    }
  }
}

export interface VirtualRowsProps<T, D> {
  items: readonly T[];
  /** Stable (a module's function), as `VirtualMasonry`'s. */
  keyOf: (item: T) => string;
  /** The card's width over its height: its shape, known before its picture loads. Stable. */
  ratioOf: (item: T) => number;
  /** One card, as `VirtualMasonry`'s: a module's component reading its item and `data` only. */
  card: CardType<T, D>;
  /** What every card reads besides its item, memoized by the screen. */
  data: D;
  /** The rows' height: the page's persisted setting (`libraryRowHeight`). */
  height: number;
  /** The element that scrolls the rows; null until it mounts. */
  root: HTMLElement | null;
  /** Names the gallery for a screen reader, as a group. */
  label?: string;
  gap?: number;
}

/**
 * Live's rows layout (spec library-rows) for a long list, as `VirtualMasonry` is its masonry:
 * every card's cell is placed, only those within a screen of the view hold their card, and the
 * rest are grey tiles. Until its place under the view is first read no card is mounted; where
 * nothing lays out (jsdom, a hidden scroller) every card is in the flow, in live's own flex rows.
 */
export function VirtualRows<T, D>(props: VirtualRowsProps<T, D>) {
  const { items, keyOf, ratioOf, card, data, height, root, label, gap = 4 } = props;
  const [grid, attach, width] = useGrid();
  const view = useView(root, grid);
  const ratios = useMemo(() => items.map(ratioOf), [items, ratioOf]);
  const rows = useMemo(() => placeRows(ratios, width ?? 0, height, gap), [ratios, width, height, gap]);
  const named = label === undefined ? {} : { role: "group", "aria-label": label };
  if (width === 0 || view === NO_LAYOUT) {
    const flow = { gap, "--rows-height": `${height}px` } as CSSProperties;
    return (
      <div ref={attach} className={classes.flow} style={flow} {...named}>
        {items.map((item, at) => (
          <div key={keyOf(item)} className={classes.flowCell} style={{ "--rows-ar": ratios[at] } as CSSProperties}>
            <Slot card={card} item={item} data={data} />
          </div>
        ))}
        <div className={classes.phantom} aria-hidden />
      </div>
    );
  }
  const total = rows.length === 0 ? 0 : rows.length * (height + gap) - gap;
  const placed = width !== null && width > 0 && view !== null ? view : null;
  return (
    <div ref={attach} className={classes.rows} style={{ height: total }} {...named}>
      {placed === null
        ? null
        : rows.flatMap((row) => {
            const near = row.top + height >= placed.top && row.top <= placed.bottom;
            return row.cells.map((cell) => {
              const item = items[cell.index]!;
              return (
                <div
                  key={keyOf(item)}
                  className={near ? classes.cell : `${classes.cell} ${masonry.tile}`}
                  style={{ top: row.top, left: cell.left, width: cell.width, height }}
                  aria-hidden={near ? undefined : true}
                >
                  {near ? <Slot card={card} item={item} data={data} /> : null}
                </div>
              );
            });
          })}
    </div>
  );
}
